/* Items for functions to use */

import { iFrameWrapper } from './journal';
import { variantFunctions } from './variantFunctions';

// Select the container holding product information.
const productInfoContainer = document.querySelector('.product__info-container');
if (!productInfoContainer) return;

// UI elements to update based on stock status.
const noteMadeToOrder = productInfoContainer.querySelector(
  '.note-made-to-order'
);
const noteInStock = productInfoContainer.querySelector('.note-in-stock');
const stockAmount = productInfoContainer.querySelector('[data-stockamount]');
const madeToOrderNotice = productInfoContainer.querySelector(
  '[data-madetoordernotice]'
);
const priceInStock = productInfoContainer.querySelector('.in-stock');
const priceMadeToOrder = productInfoContainer.querySelector('.made-to-order');
const pickUpInStore = productInfoContainer.querySelector(
  '.product__pickup-availabilities'
);

// Extract initial variant ID, slug, and other relevant elements.
const initialVariantId = productInfoContainer.querySelector(
  '[data-initialvariantid]'
).dataset.initialvariantid;
const slug = productInfoContainer.querySelector('[data-slug]').dataset.slug;
const swatchOption = productInfoContainer.querySelector(
  '[data-swatch-dropdown]'
);

/**
 * Fetches and updates variant information based on the current URL or initial variant.
 */
const updateVariantInfo = () => {
  const variantId = getVariantIdFromUrl() || parseInt(initialVariantId);
  fetchVariantData(slug, variantId);
};

/**
 * Extracts the variant ID from the current URL.
 * @return {number|null} Variant ID if present in the URL, otherwise null.
 */
const getVariantIdFromUrl = () => {
  const urlArray = window.location.href.split('variant=');
  return urlArray.length > 1 ? parseInt(urlArray[1]) : null;
};

/**
 * Fetches product data and updates the UI based on the variant's inventory status.
 * @param {string} slug - Product slug.
 * @param {number} variantId - ID of the variant to fetch.
 */
const fetchVariantData = (slug, variantId) => {
  fetch(`/products/${slug}.js`)
    .then((response) => response.json())
    .then((product) => {
      updateUIBasedOnInventory(product, variantId);
    })
    .catch((error) => console.error('Error fetching product details:', error));
};

/**
 * Updates the UI elements based on the inventory of the selected variant.
 * @param {object} product - Product data.
 * @param {number} variantId - ID of the selected variant.
 */
const updateUIBasedOnInventory = (product, variantId) => {
  const variant = product.variants.find((v) => v.id === variantId);
  if (!variant) {
    console.log('Variant not found');
    return;
  }

  const inventory = variant.inventory_quantity;
  if (inventory > 0) {
    // Update UI for in-stock items.
    updateUIForInStockItems(inventory);
  } else {
    // Update UI for made-to-order items.
    updateUIForMadeToOrderItems(variant.inventory_policy, inventory);
  }
};

/**
 * Updates the UI for in-stock items.
 * @param {number} inventory - Number of items in stock.
 */
const updateUIForInStockItems = (inventory) => {
  if (noteInStock) {
    noteInStock.classList.remove('x-hidden');
  }
  if (noteMadeToOrder) {
    noteMadeToOrder.classList.add('x-hidden');
  }
  priceInStock.classList.remove('x-hidden');
  priceMadeToOrder.classList.add('x-hidden');
  stockAmount.innerHTML = inventory;
  if (madeToOrderNotice) {
    madeToOrderNotice.innerHTML = 'This product is: In Stock';
  }
  pickUpInStore.classList.remove('x-hidden');
};

/**
 * Updates the UI for made-to-order items.
 */
const updateUIForMadeToOrderItems = (inventoryPolicy, inventory) => {
  if (noteMadeToOrder) {
    noteMadeToOrder.classList.remove('x-hidden');
  }
  if (noteInStock) {
    noteInStock.classList.add('x-hidden');
  }
  if (inventoryPolicy === 'continue' && inventory <= 0) {
    priceMadeToOrder.classList.remove('x-hidden');
  }
  priceInStock.classList.add('x-hidden');
  if (madeToOrderNotice) {
    madeToOrderNotice.innerHTML = 'This product is: Made to Order';
  }
  pickUpInStore.classList.add('x-hidden');
};

/**
 * Update lead times and stock information based on the selected product variant.
 */
export const updateLeadTimesAndStockInfo = () => {
  // Initialize by updating the variant information.
  updateVariantInfo();

  // Event listeners for changes in variant selection.
  swatchOption?.addEventListener('change', () =>
    setTimeout(() => {
      updateVariantInfo();
    }, 500)
  );
  document.addEventListener('change', (e) => {
    if (
      e.target.closest('[data-swatch]') ||
      e.target.closest('[data-variant-type]')
    ) {
      setTimeout(() => {
        updateVariantInfo();
      }, 500);
    }
  });
  // To make the price update in button on inventory change //
  document.addEventListener('click', (e) => {
    if (e.target.closest('.product-form__quantity .quantity__button')) {
      const selectTarget = document.querySelector('[data-variant-type]'); // Adjusted for clarity

      if (!selectTarget) {
        const addButtonText = document.querySelector('[name="add"] > span');
        let variantPrice = document.querySelector(
          '.price__container .money'
        ).innerHTML;
        variantPrice = variantPrice.split(' ')[0];
        variantPrice = variantPrice.substring(1);
        variantPrice = parseFloat(variantPrice).toFixed(2) * 100;
        const quantity = document.querySelector(
          '.product-form__quantity .quantity__input'
        ).value;
        const priceSum = (variantPrice * quantity) / 100;
        const currencySymbol = document
          .querySelector('.price__container .money')
          .textContent.split('')[0];

        // If amount is above 1000, add comma
        if (priceSum >= 1000) {
          addButtonText.textContent = `${
            window.variantStrings.addToCart
          } | ${currencySymbol}${priceSum
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
        } else {
          addButtonText.textContent = `${
            window.variantStrings.addToCart
          } | ${currencySymbol}${priceSum.toFixed(2)}`;
        }
      }

      selectTarget?.dispatchEvent(
        new Event('change', { bubbles: true, composed: true })
      ); // Ensure the event crosses Shadow DOM boundaries
    }
  });
};

const hideUnavailableVariantsOptions = (product) => {
  const variantId = getVariantIdFromUrl() || parseInt(initialVariantId);

  const variant = product.variants.find((v) => v.id === variantId);
  const variants = product.variants;
  const firstSelector = document.querySelector(
    'variant-selects .product-form__input select'
  ).value;
  const option2Selector = document.querySelector(
    'variant-selects .product-form__input:nth-child(2) select'
  );
  const option3Selector = document.querySelector(
    'variant-selects .product-form__input:nth-child(3) select'
  );
  const swatchOptions = document.querySelectorAll('[data-swatch-button]');
  // Variants filtered by the first selector
  const filteredVariants = variants.filter(
    (variant) => variant.option1 === firstSelector
  );

  const option1array = [];
  const option2array = [];
  const option3array = [];

  filteredVariants.forEach((variant) => {
    const variants = [variant.option1, variant.option2, variant.option3];

    option1array.push(variants[0]);
    option2array.push(variants[1]);
    option3array.push(variants[2]);
  });

  console.log(
    'option1array',
    option1array,
    'option2array',
    option2array,
    'option3array',
    option3array
  );

  // if swatchoptions span text are present, hide options that are not in the filtered array
  if (
    (swatchOptions.length > 0 && option2array.length > 1) ||
    (swatchOptions.length > 0 &&
      option2array.length > 1 &&
      option3array.length > 1)
  ) {
    let isSwatch = false;
    let singleOption = false;
    let firstOptionInSwatch = swatchOptions[0]
      .querySelector('.swatch-label')
      .textContent.trim();
    if (option2array.length < 1 && option3array.length < 1) {
      singleOption = true;
    }
    if (
      option2array.length >= 1 &&
      firstOptionInSwatch === product.options[1].values[0]
    ) {
      isSwatch = true;
    }

    console.log('isSwatch', isSwatch, 'isSingleOption', singleOption);
    swatchOptions.forEach((swatchOption, index) => {
      const span = swatchOption
        .querySelector('.swatch-label')
        .textContent.trim();
      if (!span) return;
      if (singleOption) {
        if (
          product.variants[index].inventory_quantity <= 0 &&
          product.variants[index].inventory_policy === 'deny'
        ) {
          swatchOption.style.display = 'none';
        } else {
          swatchOption.style.display = 'block';
        }
      } else {
        if (isSwatch) {
          if (option2array.length >= 1 && !option2array.includes(span)) {
            swatchOption.style.display = 'none';
            span.textContent = 'Unavailable';
          } else {
            swatchOption.style.display = 'block';
          }
        } else {
          if (option3array.length >= 1 && !option3array.includes(span)) {
            swatchOption.style.display = 'none';
          } else {
            swatchOption.style.display = 'block';
          }
        }
      }
    });
  }

  // Run through the second selector and disable options that are not in the filtered array
  if (option2Selector) {
    disableOptions(option2Selector, option2array);
  }
  if (option3Selector) {
    disableOptions(option3Selector, option3array);
  }

  const variantArray = [];

  const selectors = document.querySelectorAll('[data-variant-type]');
  selectors.forEach((selector) => {
    const variantValue = selector.value;
    variantArray.push(variantValue);
  });

  const allVariants = product.variants;

  const findVariant = allVariants.filter((variant) => {
    const variants = [variant.option1, variant.option2, variant.option3];
    return variantArray.every((value, index) => value === variants[index]);
  });

  const selectedVariant = findVariant[0];

  // Update the url so that the correct variant is added after ?variant=

  const url = window.location.href;
  // update url with variant id
  const newUrl = url.split('?')[0] + `?variant=${selectedVariant.id}`;
  window.history.pushState({ path: newUrl }, '', newUrl);

  const productForm = document.querySelector('.product__info-container');
  if (!productForm) return;

  productForm
    .querySelector('div.no-js-hidden')
    .classList.remove('visibility-hidden');

  const addButton = productForm.querySelector('[name="add"]');
  const addButtonText = productForm.querySelector('[name="add"] > span');
  if (!addButton) return;

  let newPrice = selectedVariant.price / 100;
  newPrice = newPrice.toFixed(2);
  let saleComparePrice = selectedVariant.compare_at_price / 100;
  saleComparePrice = saleComparePrice.toFixed(2);

  // Select the element containing the price

  let variantPriceElement =
    document.querySelector('.price__regular .price-item--regular .money') ||
    document.querySelector('.price__regular .price-item--regular');
  let saleComparePriceElement =
    document.querySelector('.price__sale .price-item--regular .money') ||
    document.querySelector('.price__sale .price-item--regular');
  let salePriceElement =
    document.querySelector('.price__sale .price-item--sale .money') ||
    document.querySelector('.price__sale .price-item--sale');

  let currentPriceContent = variantPriceElement.textContent; // e.g., "£10.00 GBP"

  function updatePriceContent(currentPriceContent, newPrice) {
    // Extracting the currency symbol
    let currencySymbol = currentPriceContent.match(/^\D+/)[0].trim();

    // Extracting the currency acronym
    let currencyAcronym = currentPriceContent.match(/\b[A-Z]{3}\b/)[0];

    // Attempt to determine the user's locale from the browser, with a fallback
    let userLocale = navigator.language || 'en-GB'; // Fallback to 'en-GB'

    // Formatting newPrice with commas and using the user's locale for formatting
    let formattedNewPrice = new Intl.NumberFormat(userLocale, {
      style: 'currency',
      currency: currencyAcronym,
      minimumFractionDigits: 2,
    }).format(newPrice);

    // Removing the automatically added currency symbol and replacing it with the extracted one
    formattedNewPrice = formattedNewPrice.replace(/^\D+/, currencySymbol);

    // Constructing the newPriceContent without the currency code as it's not added by Intl.NumberFormat
    let newPriceContent = `${formattedNewPrice} ${currencyAcronym}`;
    return newPriceContent;
  }
  if (variantPriceElement.textContent) {
    variantPriceElement.textContent = updatePriceContent(
      currentPriceContent,
      newPrice
    );
  }
  if (saleComparePriceElement.textContent) {
    saleComparePriceElement.textContent = updatePriceContent(
      currentPriceContent,
      saleComparePrice
    );
  }
  salePriceElement.textContent = updatePriceContent(
    currentPriceContent,
    newPrice
  );

  // Set the stock amount //
  const stockAmount = findVariant[0].inventory_quantity;
  const stockAmountElement = document.querySelector('[data-stockamount]');
  stockAmountElement.textContent = stockAmount;

  const quantity = document.querySelector(
    '.product-form__quantity .quantity__input'
  ).value;
  const priceSum = newPrice * quantity;
  let currencySymbol;
  if (document.querySelector('.price__container .money')) {
    currencySymbol = document
      .querySelector('.price__container .money')
      .textContent.split('')[0];
  } else {
    currencySymbol = document
      .querySelector('.price__container [data-price]')
      .textContent.split('')[0];
  }

  if (variant.inventory_quantity === 0 && variant.inventory_policy === 'deny') {
    addButton.setAttribute('disabled', 'disabled');
    addButtonText.textContent = 'Sold Out';
    setTimeout(() => {
      if (
        product.variants[0].inventory_quantity !== 0 &&
        product.variants[0].inventory_policy !== 'deny'
      ) {
        addButton.setAttribute('disabled', 'disabled');
        addButtonText.textContent = 'Sold Out';
      }
    }, 1000);
  } else {
    addButton.classList.add('x-bg-black', 'hover:x-bg-blue-900');
    addButton.classList.remove('x-bg-red-500');
    addButton.removeAttribute('disabled');
    if (priceSum >= 1000) {
      addButtonText.textContent = `${
        window.variantStrings.addToCart
      } | ${currencySymbol}${priceSum
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    } else {
      addButtonText.textContent = `${
        window.variantStrings.addToCart
      } | ${currencySymbol}${priceSum.toFixed(2)}`;
    }
  }

  // // Fire the overall function to update the everything else
};

// Option disable function //

const disableOptions = (selector, filteredArray) => {
  if (!selector) return;
  selector.querySelectorAll('option').forEach((option) => {
    if (!filteredArray.includes(option.value)) {
      option.disabled = true;
      // option.style.display = 'none';
    } else {
      option.disabled = false;
      // option.style.display = 'block';
    }
  });
  // If the selected option is disabled, reset it to the first available option
  if (selector.querySelector('option:checked').disabled) {
    selector.value = filteredArray[0];
  }
};

export const pdp = () => {
  updateLeadTimesAndStockInfo();
  variantFunctions(slug);
  // hideUnavailableVariants();
  iFrameWrapper('.product__media-wrapper');
};
